import { LockIcon } from "@admin/pages/objects-configure/assets/Icon";
import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import type { HttpError } from "@refinedev/core";
import { useUpdate } from "@refinedev/core";
import { Button, Spin, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";

import { EAZY_COLORS } from "../constants";
import { formatDeviceContractForDisplay } from "../utils/deviceContract";
import { isValidBuildingItem } from "../utils/validation";
import type { BuildingItem, EazySyncInfo } from "./BuildingSearchResults";

const { Text } = Typography;

interface UpdateRegistrationPanelProps {
  registeredTarget: BuildingItem | null;
  switchTarget: BuildingItem | null;
  onRegisteredTargetChange: (target: BuildingItem | null) => void;
  onSwitchTargetChange: (target: BuildingItem | null) => void;
  onRefresh?: () => void;
  submitResult?: "success" | "error" | null;
  errorMessage?: string;
  onSubmitResultChange?: (result: "success" | "error" | null) => void;
  onErrorMessageChange?: (message: string) => void;
}

export const UpdateRegistrationPanel = ({
  registeredTarget,
  switchTarget,
  onRegisteredTargetChange,
  onSwitchTargetChange,
  onRefresh,
  submitResult: externalSubmitResult,
  errorMessage: externalErrorMessage,
  onSubmitResultChange,
  onErrorMessageChange,
}: UpdateRegistrationPanelProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<"success" | "error" | null>(externalSubmitResult ?? null);
  const [errorMessage, setErrorMessage] = useState<string>(externalErrorMessage ?? "");
  const [registeredValidationError, setRegisteredValidationError] = useState<string>("");
  const [switchValidationError, setSwitchValidationError] = useState<string>("");
  const { mutateAsync: updateAsync } = useUpdate();

  const updateSubmitResult = (result: "success" | "error" | null) => {
    setSubmitResult(result);
    onSubmitResultChange?.(result);
  };

  const updateErrorMessage = (message: string) => {
    setErrorMessage(message);
    onErrorMessageChange?.(message);
  };

  useEffect(() => {
    if (externalSubmitResult !== undefined) {
      setSubmitResult(externalSubmitResult);
    }
  }, [externalSubmitResult]);

  useEffect(() => {
    if (externalErrorMessage !== undefined) {
      setErrorMessage(externalErrorMessage);
    }
  }, [externalErrorMessage]);

  const isRegisteredStatus = (status?: EazySyncInfo["status"]) => {
    return status === "CREATED" || status === "UPDATED" || status === "UPDATE_FAILED";
  };

  const isValidForRegistered = (item: BuildingItem) => {
    return isRegisteredStatus(item.eazy_sync?.status);
  };

  const isValidForSwitch = (item: BuildingItem) => {
    const status = item.eazy_sync?.status;
    return !status || status === "CREATE_FAILED";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, type: "registered" | "switch") => {
    e.preventDefault();
    if (isDisabled) return;
    const dataText = e.dataTransfer.getData("text/plain");
    if (!dataText) return;

    try {
      const parsedData = JSON.parse(dataText);
      if (!isValidBuildingItem(parsedData)) {
        console.error("Invalid building item data:", parsedData);
        return;
      }

      const data: BuildingItem = parsedData;
      if (type === "registered") {
        onRegisteredTargetChange(data);
        if (isValidForRegistered(data)) {
          setRegisteredValidationError("");
        } else {
          setRegisteredValidationError("選択された情報は変更要件を満たしていません");
        }
      } else {
        onSwitchTargetChange(data);
        if (isValidForSwitch(data)) {
          setSwitchValidationError("");
        } else {
          setSwitchValidationError("選択された情報は変更要件を満たしていません");
        }
      }
    } catch (error) {
      console.error("Failed to parse drag data:", error);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (isDisabled) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.dataTransfer.dropEffect = "move";
  };

  const getStatusTag = (status?: EazySyncInfo["status"]) => {
    if (!status) return null;

    if (status === "CREATED" || status === "UPDATED" || status === "UPDATE_FAILED") {
      return (
        <Tag bordered={false} color="processing">
          登録済
        </Tag>
      );
    }

    if (status === "DELETED") {
      return (
        <Tag bordered={false} color="error">
          削除済
        </Tag>
      );
    }

    return null;
  };

  const hasBothTargets = useMemo(
    () => registeredTarget !== null && switchTarget !== null,
    [registeredTarget, switchTarget]
  );
  const hasValidationErrors = useMemo(
    () => registeredValidationError !== "" || switchValidationError !== "",
    [registeredValidationError, switchValidationError]
  );
  const canShowPreview = useMemo(() => hasBothTargets && !hasValidationErrors, [hasBothTargets, hasValidationErrors]);
  const isDisabled = useMemo(() => submitResult !== null, [submitResult]);

  const handleSubmit = async () => {
    if (!registeredTarget || !switchTarget) return;

    const propertyId = registeredTarget.property.id;
    const registeredBuildingId = registeredTarget.building_id;
    const newBuildingId = switchTarget.building_id;

    try {
      setIsSubmitting(true);
      updateErrorMessage("");
      await updateAsync({
        resource: "properties",
        id: `${propertyId}/buildings/eazy_replace`,
        values: {
          registered_building_id: registeredBuildingId,
          new_building_id: newBuildingId,
        },
        meta: { method: "post" },
      });
      updateSubmitResult("success");
      setRegisteredValidationError("");
      setSwitchValidationError("");

      const managementId = registeredTarget.eazy_sync?.management_id || "";

      const updatedSwitchTarget: BuildingItem = {
        ...switchTarget,
        eazy_sync: {
          ...switchTarget.eazy_sync,
          management_id: managementId,
          status: "UPDATED",
        },
      };

      const updatedRegisteredTarget: BuildingItem = {
        ...registeredTarget,
        eazy_sync: undefined,
      };

      onRegisteredTargetChange(updatedRegisteredTarget);
      onSwitchTargetChange(updatedSwitchTarget);
      // 更新完成后刷新左边的搜索结果
      onRefresh?.();
    } catch (error) {
      const httpError = error as HttpError;
      let errorMsg = "操作が失敗しました";

      if (httpError.errors) {
        if (typeof httpError.errors === "string") {
          errorMsg = httpError.errors;
        } else if (httpError.errors.msg) {
          errorMsg = String(httpError.errors.msg);
        } else if (httpError.errors.message) {
          errorMsg = String(httpError.errors.message);
        }
      }
      const prefix = "failed to update device in Eazy: update device failed: ";
      if (errorMsg.includes(prefix)) {
        errorMsg = errorMsg.substring(errorMsg.indexOf(prefix) + prefix.length);
      } else {
        errorMsg = "操作が失敗しました";
      }

      updateErrorMessage(errorMsg);
      updateSubmitResult("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    onRegisteredTargetChange(null);
    onSwitchTargetChange(null);
    updateSubmitResult(null);
    updateErrorMessage("");
    setRegisteredValidationError("");
    setSwitchValidationError("");
    // handleConfirm 只是清除状态，刷新已经在 handleSubmit 成功后执行了
  };

  const convertDeviceContract = (deviceContract: string): string => {
    if (deviceContract === "dearisController") {
      return "dearis";
    }
    if (deviceContract === "PabbitLite") {
      return "PP03A";
    }
    return deviceContract;
  };

  const renderPreview = () => {
    if (submitResult) {
      if (!registeredTarget || !switchTarget) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "24px" }}>
            {isSubmitting ? (
              <div
                className="flex items-center justify-center"
                style={{
                  height: "52px",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Spin size="small" style={{ marginRight: "8px" }} />
                <Text
                  style={{
                    color: "#6A7282",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                    textAlign: "center",
                  }}
                >
                  提出中
                </Text>
              </div>
            ) : (
              <>
                <div
                  className="flex items-center justify-center"
                  style={{
                    minHeight: "52px",
                    maxHeight: "120px",
                    backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
                    padding: "12px 16px",
                    overflowY: "auto",
                  }}
                >
                  {submitResult === "success" ? (
                    <Text
                      style={{
                        color: EAZY_COLORS.SUCCESS,
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        textAlign: "center",
                      }}
                    >
                      更新に成功しました
                    </Text>
                  ) : (
                    <Text
                      style={{
                        color: EAZY_COLORS.ERROR,
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        textAlign: "center",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        maxWidth: "100%",
                      }}
                    >
                      {errorMessage || "操作が失敗しました"}
                    </Text>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button onClick={handleConfirm} type="primary">
                    確定
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      }
    } else {
      if (!canShowPreview || !registeredTarget || !switchTarget) return null;
    }

    const managementId = switchTarget.eazy_sync?.management_id || registeredTarget.eazy_sync?.management_id || "";
    const registeredBuildingId = registeredTarget.building_id || "";
    const switchBuildingId = switchTarget.building_id || "";
    const registeredDeviceContract = registeredTarget.property.device_contracts[0] || "";
    const switchDeviceContract = switchTarget.property.device_contracts[0] || "";

    const registeredFullAddress = registeredTarget.address?.full_address || "";
    const registeredBuildingName = registeredTarget.name || "";
    const registeredAddress = registeredFullAddress
      ? `${registeredFullAddress} ${registeredBuildingName}`
      : registeredBuildingName;

    const switchFullAddress = switchTarget.address?.full_address || "";
    const switchBuildingName = switchTarget.name || "";
    const switchAddress = switchFullAddress ? `${switchFullAddress} ${switchBuildingName}` : switchBuildingName;

    const isBuildingIdChanged = submitResult === "success" ? false : registeredBuildingId !== switchBuildingId;
    const isAddressChanged = submitResult === "success" ? false : registeredAddress !== switchAddress;
    const isDeviceContractChanged =
      submitResult === "success" ? false : registeredDeviceContract !== switchDeviceContract;

    const displaySwitchDeviceContract = convertDeviceContract(switchDeviceContract);

    return (
      <>
        <Text
          style={{
            color: "#000000",
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "24px",
            display: "block",
            marginTop: "24px",
          }}
        >
          更新内容のプレビュー
        </Text>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "16px" }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <Text
                  style={{
                    color: "#000000",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "22px",
                  }}
                >
                  管理ID: {managementId}
                </Text>
              </div>
              <div>
                <Text
                  style={{
                    color: "#000000",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "22px",
                  }}
                >
                  デジタルキー:{" "}
                  <Text
                    style={{
                      color: isBuildingIdChanged ? EAZY_COLORS.SUCCESS : EAZY_COLORS.TEXT_PRIMARY,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    {switchBuildingId}
                    {isBuildingIdChanged && (
                      <Text
                        style={{
                          color: EAZY_COLORS.SUCCESS,
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "22px",
                        }}
                      >
                        {" "}
                        (変更)
                      </Text>
                    )}
                  </Text>
                </Text>
              </div>
              <div>
                <Text
                  style={{
                    color: "#000000",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "22px",
                  }}
                >
                  住所:{" "}
                  <Text
                    style={{
                      color: isAddressChanged ? EAZY_COLORS.SUCCESS : EAZY_COLORS.TEXT_PRIMARY,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    {switchAddress}
                    {isAddressChanged && (
                      <Text
                        style={{
                          color: EAZY_COLORS.SUCCESS,
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "22px",
                        }}
                      >
                        {" "}
                        (変更)
                      </Text>
                    )}
                  </Text>
                </Text>
              </div>
              <div>
                <Text
                  style={{
                    color: "#000000",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "22px",
                  }}
                >
                  機種:{" "}
                  <Text
                    style={{
                      color: isDeviceContractChanged ? EAZY_COLORS.SUCCESS : EAZY_COLORS.TEXT_PRIMARY,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    {displaySwitchDeviceContract}
                    {isDeviceContractChanged && (
                      <Text
                        style={{
                          color: EAZY_COLORS.SUCCESS,
                          fontWeight: 400,
                          fontSize: "14px",
                          lineHeight: "22px",
                        }}
                      >
                        {" "}
                        (変更)
                      </Text>
                    )}
                  </Text>
                </Text>
              </div>
            </div>
          </div>
          {isSubmitting ? (
            <div
              className="flex items-center justify-center"
              style={{
                height: "52px",
                backgroundColor: "#F9FAFB",
              }}
            >
              <Spin size="small" style={{ marginRight: "8px" }} />
              <Text
                style={{
                  color: "#6A7282",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  textAlign: "center",
                }}
              >
                提出中
              </Text>
            </div>
          ) : submitResult ? (
            <>
              <div
                className="flex items-center justify-center"
                style={{
                  minHeight: "52px",
                  maxHeight: "120px",
                  backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
                  padding: "12px 16px",
                  overflowY: "auto",
                }}
              >
                {submitResult === "success" ? (
                  <Text
                    style={{
                      color: EAZY_COLORS.SUCCESS,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      textAlign: "center",
                    }}
                  >
                    更新に成功しました
                  </Text>
                ) : (
                  <Text
                    style={{
                      color: EAZY_COLORS.ERROR,
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "20px",
                      textAlign: "center",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      maxWidth: "100%",
                    }}
                  >
                    {errorMessage || "操作が失敗しました"}
                  </Text>
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button onClick={handleConfirm} type="primary">
                  確定
                </Button>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button icon={<SaveOutlined />} onClick={handleSubmit} type="primary">
                更新に提出
              </Button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}>
        <div
          className={`flex flex-col items-center justify-center rounded-[10px] bg-white ${isDisabled ? "cursor-not-allowed" : ""}`}
          onDragOver={isDisabled ? undefined : handleDragOver}
          onDrop={isDisabled ? undefined : (e) => handleDrop(e, "registered")}
          style={{
            height: "136px",
            border: `2px solid ${registeredValidationError ? EAZY_COLORS.ERROR : EAZY_COLORS.BORDER_DEFAULT}`,
            borderTop: `2px solid ${registeredValidationError ? EAZY_COLORS.ERROR : EAZY_COLORS.BORDER_DEFAULT}`,
            position: "relative",
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "default",
          }}
        >
          {registeredTarget ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                paddingTop: "16px",
                paddingBottom: "16px",
                paddingLeft: "32px",
                paddingRight: "32px",
                position: "relative",
                cursor: isDisabled ? "not-allowed" : "default",
              }}
            >
              {registeredTarget && !isDisabled && (
                <CloseOutlined
                  onClick={() => {
                    setRegisteredValidationError("");
                    onRegisteredTargetChange(null);
                  }}
                  style={{
                    cursor: "pointer",
                    color: "#6A7282",
                    fontSize: "16px",
                    position: "absolute",
                    top: "16px",
                    right: "32px",
                    zIndex: 1,
                  }}
                />
              )}
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: "8px", width: "100%", paddingRight: "48px" }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#000000",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    flex: 1,
                  }}
                >
                  {registeredTarget.name}
                </Text>
              </div>
              {registeredTarget.property.device_contracts[0] && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                  <LockIcon height="14" width="14" />
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 400,
                      fontSize: "13px",
                      lineHeight: "22px",
                    }}
                  >
                    {formatDeviceContractForDisplay(registeredTarget.property.device_contracts[0])}
                  </Text>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "8px",
                  width: "100%",
                  marginRight: "0",
                }}
              >
                <Text
                  style={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                  }}
                >
                  棟ID: {registeredTarget.building_id}
                </Text>
                <div style={{ marginLeft: "auto" }}>{getStatusTag(registeredTarget.eazy_sync?.status)}</div>
              </div>
            </div>
          ) : (
            <>
              <Text
                style={{
                  color: "#364153",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  textAlign: "center",
                  display: "block",
                  marginBottom: "8px",
                  cursor: isDisabled ? "not-allowed" : "default",
                }}
              >
                登録済の対象
              </Text>
              <Text
                style={{
                  color: "#6A7282",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "16px",
                  textAlign: "center",
                  cursor: isDisabled ? "not-allowed" : "default",
                }}
              >
                登録済の棟情報をここにドラッグしてください
              </Text>
            </>
          )}
        </div>

        <div
          className={`flex flex-col items-center justify-center rounded-[10px] bg-white ${isDisabled ? "cursor-not-allowed" : ""}`}
          onDragOver={isDisabled ? undefined : handleDragOver}
          onDrop={isDisabled ? undefined : (e) => handleDrop(e, "switch")}
          style={{
            height: "136px",
            border: `2px solid ${switchValidationError ? EAZY_COLORS.ERROR : EAZY_COLORS.BORDER_DEFAULT}`,
            borderTop: `2px solid ${switchValidationError ? EAZY_COLORS.ERROR : EAZY_COLORS.BORDER_DEFAULT}`,
            position: "relative",
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "default",
          }}
        >
          {switchTarget ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                paddingTop: "16px",
                paddingBottom: "16px",
                paddingLeft: "32px",
                paddingRight: "32px",
                position: "relative",
                cursor: isDisabled ? "not-allowed" : "default",
              }}
            >
              {switchTarget && !isDisabled && (
                <CloseOutlined
                  onClick={() => {
                    setSwitchValidationError("");
                    onSwitchTargetChange(null);
                  }}
                  style={{
                    cursor: "pointer",
                    color: "#6A7282",
                    fontSize: "16px",
                    position: "absolute",
                    top: "16px",
                    right: "32px",
                    zIndex: 1,
                  }}
                />
              )}
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: "8px", width: "100%", paddingRight: "48px" }}
              >
                <Text
                  style={{
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    color: "#000000",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    flex: 1,
                  }}
                >
                  {switchTarget.name}
                </Text>
              </div>
              {switchTarget.property.device_contracts[0] && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                  <LockIcon height="14" width="14" />
                  <Text
                    style={{
                      color: "#6A7282",
                      fontWeight: 400,
                      fontSize: "13px",
                      lineHeight: "22px",
                    }}
                  >
                    {formatDeviceContractForDisplay(switchTarget.property.device_contracts[0])}
                  </Text>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "8px",
                  width: "100%",
                  marginRight: "0",
                }}
              >
                <Text
                  style={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "20px",
                  }}
                >
                  棟ID: {switchTarget.building_id}
                </Text>
                <div style={{ marginLeft: "auto" }}>{getStatusTag(switchTarget.eazy_sync?.status)}</div>
              </div>
            </div>
          ) : (
            <>
              <Text
                style={{
                  color: "#364153",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  textAlign: "center",
                  display: "block",
                  marginBottom: "8px",
                  cursor: isDisabled ? "not-allowed" : "default",
                }}
              >
                切替の対象(機種を変更された)
              </Text>
              <Text
                style={{
                  color: "#6A7282",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "16px",
                  textAlign: "center",
                  cursor: isDisabled ? "not-allowed" : "default",
                }}
              >
                切り替える棟情報をここにドラッグしてください
              </Text>
            </>
          )}
        </div>
      </div>

      {hasValidationErrors ? (
        <div
          className="flex items-center justify-center"
          style={{
            minHeight: "52px",
            backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
            marginTop: "24px",
            padding: "12px 16px",
          }}
        >
          <Text
            style={{
              color: EAZY_COLORS.ERROR,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              textAlign: "center",
              wordBreak: "break-word",
            }}
          >
            {registeredValidationError || switchValidationError}
          </Text>
        </div>
      ) : !hasBothTargets ? (
        <div
          className="flex items-center justify-center"
          style={{
            height: "52px",
            backgroundColor: EAZY_COLORS.BACKGROUND_GRAY,
            marginTop: "24px",
          }}
        >
          <Text
            style={{
              color: EAZY_COLORS.TEXT_SECONDARY,
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              textAlign: "center",
            }}
          >
            両方の対象をドラッグすると、自動的にプレビューが生成されます
          </Text>
        </div>
      ) : null}

      {renderPreview()}
    </div>
  );
};
